/**
 * @file        src/components/promo/PromoButton.tsx
 * @description The persistent, compact control that opens the offer window. A horizontal pill —
 *              gift glyph and label — fixed near the right edge on desktop and the left edge on
 *              mobile. It reports a click upward and nothing else: it does not own the window and
 *              never imports PromoModal, so the display logic stays in PromoGate.
 * @universal   knowledge/universals/components.md#promobutton
 * @usage <PromoButton onClick={() => setOpen(true)} />
 * @rule        NO NUMBER ON THE PILL since 2026-10-20 (ADR N-PROMO-PERMANENT-1): the offer is
 *              permanent and has no quantity, so the pill carries the label only.
 */

"use client";

import { Gift } from "lucide-react";
import { PROMO_PROGRAM } from "@/lib/promo/promoConfig";

interface PromoButtonProps {
  /** Called on click. This component does not know what opens. */
  onClick: () => void;
}

/**
 * Floating pill that opens the promo offer window.
 *
 * @param props see {@link PromoButtonProps}
 * @returns the pill
 */
// @rule GEOMETRIC DEPENDENCY ON FloatingMessengers — if you change that component's size or its
// edge insets, this offset is wrong and the two floating controls will touch. Both sit at z-50,
// where neither reliably wins, so overlap produces an unclickable button rather than a cosmetic
// glitch. The arithmetic, from src/components/FloatingMessengers.tsx:
//   desktop (>=sm): container `bottom-6 right-6` = 24px inset; main button `h-14` = 56px.
//       24 + 56 = 80px is the top of the FAB. Plus a 16px gap → this pill sits at bottom-24 (96px).
//   mobile  (<sm):  container `max-sm:bottom-4 max-sm:right-4` = 16px inset; button `h-12` = 48px.
//       No vertical stacking on mobile — the pill moves to the LEFT edge at the same height,
//       so the two never share a corner and no vertical gap is needed. env(safe-area-inset-bottom)
//       is added so the pill clears the OS home-gesture bar on gesture-navigation devices.
export default function PromoButton({ onClick }: PromoButtonProps) {
  const { texts } = PROMO_PROGRAM;

  // A sighted visitor sees the gift glyph next to the label; someone hearing the button alone
  // gets the offer itself. Assembled from config so it cannot drift from the window.
  const spokenLabel = `${texts.buttonLabel}: ${texts.heading}`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={spokenLabel}
      // @rule NEVER attach data-fm-hide to a position-fixed or sticky element.
      // The observer in FloatingMessengers collects its targets ONCE, in a single
      // requestAnimationFrame at mount, with no MutationObserver. A fixed element
      // is permanently intersecting the viewport, so "hide the FAB while this is
      // visible" silently becomes "hide the FAB forever" — here, for the whole
      // life of the program. That would trade the site's contact
      // channels (Telegram, WhatsApp, Max, phone) for a promotional pill.
      // Separation between this pill and the FAB is PURELY GEOMETRIC — see the
      // offset arithmetic above. The attribute is for elements that scroll in and
      // out of view, such as an inline lead form.
      className={[
        "fixed z-50 flex min-h-[44px] items-center gap-2.5 rounded-full",
        "bg-primary px-4 py-2 text-white shadow-lg",
        "transition-transform duration-200 hover:scale-105",
        // Mobile: left edge, level with the FAB, lifted clear of the home-gesture bar.
        "bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] left-4",
        // Desktop: right edge, directly above the FAB with a 16px gap (see the arithmetic above).
        "sm:bottom-24 sm:left-auto sm:right-6",
        // One subtle entrance, never a loop. Nothing here pulses or demands attention.
        "animate-in fade-in slide-in-from-bottom-2 duration-500",
      ].join(" ")}
    >
      <Gift className="h-5 w-5 shrink-0 text-secondary" aria-hidden="true" />
      <span className="text-sm font-medium">{texts.buttonLabel}</span>
    </button>
  );
}
